import type { GameStateManager } from '../game/GameState';
import type { GameData, GameSpeed } from '../game/types';
import { AudioManager } from '../audio/AudioManager';

export class MenuModal {
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;
  private audioManager: AudioManager;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onClose: () => void,
    private onControls: () => void,
    private onHowToPlay: () => void,
    private onCompendium: () => void,
    private onSpeedRestore?: (speed: GameSpeed) => void
  ) {
    this.audioManager = AudioManager.getInstance();
    this.setup();
  }

  private setup(): void {
    this.container.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('modal-backdrop')) {
        this.hide();
      }
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    // Only handle if modal is open
    if (this.container.classList.contains('hidden')) return;

    if (event.code === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }
  }

  show(): void {
    const musicVolume = this.audioManager.getMusicVolume();
    const soundVolume = this.audioManager.getSoundVolume();
    const isMuted = this.audioManager.isMuted();

    this.container.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content menu-modal" tabindex="0">
        <div class="modal-header">
          <h2>Menu</h2>
          <button class="modal-close" data-action="close">×</button>
        </div>
        <div class="modal-body">
          <div class="menu-audio-controls">
            <h3>Audio Settings</h3>
            <div class="audio-control-group">
              <label for="sound-volume">Sound Volume</label>
              <input type="range" id="sound-volume" min="0" max="100" value="${soundVolume}" class="volume-slider">
              <span class="volume-value">${soundVolume}%</span>
            </div>
            <div class="audio-control-group">
              <label for="music-volume">Music Volume</label>
              <input type="range" id="music-volume" min="0" max="100" value="${musicVolume}" class="volume-slider">
              <span class="volume-value">${musicVolume}%</span>
            </div>
            <div class="audio-control-group">
              <button class="mute-button ${isMuted ? 'muted' : ''}" data-action="toggle-mute">
                ${isMuted ? '🔇 Muted' : '🔊 Unmuted'}
              </button>
            </div>
          </div>
          
          <div class="menu-options">
            <button class="menu-option" data-action="controls">Controls</button>
            <button class="menu-option" data-action="how-to-play">How to Play</button>
            <button class="menu-option" data-action="compendium">Compendium</button>
            <button class="menu-option menu-option-continue" data-action="continue">Continue</button>
          </div>
        </div>
      </div>
    `;

    // Attach event listeners
    const closeButton = this.container.querySelector('[data-action="close"]') as HTMLElement;
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }

    const controlsButton = this.container.querySelector('[data-action="controls"]') as HTMLElement;
    if (controlsButton) {
      controlsButton.addEventListener('click', () => {
        this.hide();
        this.onControls();
      });
    }

    const howToPlayButton = this.container.querySelector('[data-action="how-to-play"]') as HTMLElement;
    if (howToPlayButton) {
      howToPlayButton.addEventListener('click', () => {
        this.hide();
        this.onHowToPlay();
      });
    }

    const compendiumButton = this.container.querySelector('[data-action="compendium"]') as HTMLElement;
    if (compendiumButton) {
      compendiumButton.addEventListener('click', () => {
        this.hide();
        this.onCompendium();
      });
    }

    const continueButton = this.container.querySelector('[data-action="continue"]') as HTMLElement;
    if (continueButton) {
      continueButton.addEventListener('click', () => this.hide());
    }

    const muteButton = this.container.querySelector('[data-action="toggle-mute"]') as HTMLElement;
    if (muteButton) {
      muteButton.addEventListener('click', () => {
        this.audioManager.toggleMute();
        this.show(); // Refresh to update mute button
      });
    }

    // Setup volume sliders
    const soundSlider = this.container.querySelector('#sound-volume') as HTMLInputElement;
    const musicSlider = this.container.querySelector('#music-volume') as HTMLInputElement;
    
    if (soundSlider) {
      soundSlider.addEventListener('input', (e) => {
        const value = parseInt((e.target as HTMLInputElement).value, 10);
        this.audioManager.setSoundVolume(value);
        const valueSpan = this.container.querySelector('#sound-volume')?.nextElementSibling as HTMLElement;
        if (valueSpan) {
          valueSpan.textContent = `${value}%`;
        }
      });
    }
    
    if (musicSlider) {
      musicSlider.addEventListener('input', (e) => {
        const value = parseInt((e.target as HTMLInputElement).value, 10);
        this.audioManager.setMusicVolume(value);
        const valueSpan = this.container.querySelector('#music-volume')?.nextElementSibling as HTMLElement;
        if (valueSpan) {
          valueSpan.textContent = `${value}%`;
        }
      });
    }

    this.container.classList.remove('hidden');
    this.gameState.setPausedByModal(true);
    
    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
    
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  hide(): void {
    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    const restoredSpeed = this.gameState.restorePauseStateAfterModal();
    if (restoredSpeed !== null && this.onSpeedRestore) {
      this.onSpeedRestore(restoredSpeed);
    }
    
    // Remove keyboard handler when modal closes
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }
}

